import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseEditCalendar } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course, CourseDateAnnotationSingle } from "@shared/models/course"
import { findCourseDatesOff } from "cc-frontend/lib/dates-off-calculator"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { assign, filter, find, map, partial } from "lodash-es"
import moment from "moment"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultCourseDateActivity from "./default-course-date-activity"
import defaultCourseDateContext from "./default-course-date-context"
import defaultCourseDateModels from "./default-course-date-models"

interface Payload {
  annotation: CourseDateAnnotationSingle
  course: Course
}

export default function courseDateAnnotationUpdate(payload: Payload): PreparedAction {
  return {
    name: "COURSE_DATE_ANNOTATION_UPDATE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let newAnnotations = map(payload.course.attributes.calendar.dateAnnotations, (annotation) => {
    return annotation.id === payload.annotation.id ? payload.annotation : annotation
  })

  let allDatesOff = findCourseDatesOff(newAnnotations)

  let patch = Patch.create("course", payload.course.id, "attributes.calendar.dateAnnotations", {
    id: payload.annotation.id,
  })
    .set("embedded", `attributes`, payload.annotation.attributes)
    .set("attributes.calendar.datesOff", allDatesOff)
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

function undoPatches(payload: Payload) {
  let allDatesOff = findCourseDatesOff(payload.course.attributes.calendar.dateAnnotations)

  // this feels heavy handed. Would be better if we just find that single annotation.
  let patch = Patch.create("course", payload.course.id)
    .set(
      "attributes.calendar.dateAnnotations",
      JSON.parse(JSON.stringify(payload.course.attributes.calendar.dateAnnotations))
    )
    .set("attributes.calendar.datesOff", allDatesOff)
    .inc("attributes.calendar.version", 1).patch

  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultCourseDateModels(payload, findFn, userId)
  let context = defaultCourseDateContext(payload, models)
  let formattedDateString = moment(payload.annotation.attributes.dateString).format(
    "ddd, MMM. D, YYYY"
  )

  let originalAnnotation = find(
    payload.course.attributes.calendar.dateAnnotations,
    (annotation) => {
      return annotation.id === payload.annotation.id
    }
  )

  let fieldsEdited: Array<string> = []
  let description
  if (originalAnnotation) {
    fieldsEdited = filter(["title", "description"], (key) => {
      return payload.annotation.attributes[key] !== originalAnnotation?.attributes?.[key]
    })
    description = `Changed the ${fieldsEdited.join(" and ")} for the note on your "${
      context.courseTitle
    }" class on ${formattedDateString}`
  } else {
    description = `Updated the note on your "${context.courseTitle}" class on ${formattedDateString}`
  }

  let newContext = assign({}, context, { fieldsEdited: fieldsEdited })

  let newEvent: CourseEditCalendar = {
    ...createBlankEvent(),
    root_id: models.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook.id),

    primary_object_type: "course",
    primary_object_id: payload.course.id,
    primary_object_specifier: "calendar",
    primary_object_id_and_ancestor_ids: [models.planbook.id, payload.course.id],

    adverbial_verb: "editing",

    adverbial_object_type: "annotation",
    adverbial_object_id: payload.annotation.id,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: [models.planbook.id, payload.course.id],
  }

  return {
    title: "Edited Note for Turned-off Class",
    titleForAnalytics: "Lesson - Update Annotation",
    description: description,
    context: newContext,
    activity: defaultCourseDateActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
